import React, { useState } from "react"
import { useMutation } from "urql"

import { Button } from "../../../components/shared/Buttons"
import { CheckBox } from "../../../components/shared/Inputs"
import { Divider, Flyout } from "../../../components/shared/Layout"
import { useToast } from "../../../contexts/ToastContext"
import CalIcon from "../ProCalendarSettings/CalIcon"

import { UPDATE_CALENDAR_WRITE_SELECTIONS_MUTATION } from "./graphql"
import { WRITE_SELECTION_TOOLTIP, providerLabel, singleWriteSelection, writeSelectableCalendars } from "./selectors"
import { CalendarConnectionSummary, MutationResultPayload } from "./types"

interface Props {
  connection: CalendarConnectionSummary | null
  visible: boolean
  onClose: () => void
  onSaved: () => void
}

const calIconProvider = (provider: string) => (provider === "microsoft" ? "live_connect" : provider)

// Multi-select "Also add Heal.me bookings here" per writable calendar.
// Read-only calendars and feeds are excluded entirely (mirrors backend
// guards). Success is only announced after the mutation confirms it.
const WriteCalendarFlyout = ({ connection, visible, onClose, onSaved }: Props) => {
  const [selectedIds, setSelectedIds] = useState<string[]>(
    () => connection?.calendars.filter((calendar) => calendar.selectedForWrite).map((calendar) => calendar.id) || []
  )
  const [saving, setSaving] = useState(false)
  const [, updateSelections] = useMutation<{ updateCalendarWriteSelections: MutationResultPayload }>(
    UPDATE_CALENDAR_WRITE_SELECTIONS_MUTATION
  )
  const { showToast } = useToast()

  if (!connection) return null

  const calendars = writeSelectableCalendars(connection)
  // Legacy Cronofy writes to a single calendar per account, so checking one
  // calendar replaces the previous choice (mirrors the backend guard).
  const singleSelect = singleWriteSelection(connection)

  const toggle = (id: string) => {
    setSelectedIds((previous) => {
      if (previous.includes(id)) return previous.filter((v) => v !== id)
      return singleSelect ? [id] : [...previous, id]
    })
  }

  const save = () => {
    setSaving(true)
    updateSelections({ connectionPublicId: connection.publicId, calendarIds: selectedIds }).then((result) => {
      setSaving(false)
      const payload = result.data?.updateCalendarWriteSelections
      if (payload?.result === "success") {
        showToast("Booking calendars updated")
        onSaved()
      } else {
        showToast({ content: payload?.errors?.[0] || "Could not update booking calendars", type: "error" })
      }
    })
  }

  return (
    <Flyout header="Where should we add Heal.me bookings?" visible={visible} closeFlyout={onClose} footer={null}>
      <div className="flex items-center gap-4">
        <CalIcon provider={calIconProvider(connection.provider)} className="" iconClassName="" />
        <div>{connection.displayIdentity || providerLabel(connection.provider)}</div>
      </div>
      <Divider className="" />
      <div className="mb-2 font-bold">Also add Heal.me bookings here</div>
      <p className="mb-4 text-sm text-gray-dark">{WRITE_SELECTION_TOOLTIP}</p>
      {calendars.length === 0 ? (
        <p className="text-sm text-gray-dark">
          This account has no calendars that can receive Heal.me bookings. Read-only calendars and feeds can still block
          conflicting times.
        </p>
      ) : (
        <ul className="flex flex-col gap-2">
          {calendars.map((calendar) => (
            <li key={calendar.id}>
              <CheckBox
                className=""
                id={`write-calendar-${calendar.id}`}
                label={calendar.displayName || "Calendar"}
                checked={selectedIds.includes(calendar.id)}
                onChange={() => toggle(calendar.id)}
              />
            </li>
          ))}
        </ul>
      )}
      {singleSelect && calendars.length > 0 && (
        <p className="mt-2 text-sm text-gray-dark">This account adds bookings to one calendar at a time.</p>
      )}
      <p className="mt-4 text-sm text-gray-dark">
        Calendars selected only for conflicts still prevent double bookings without receiving Heal.me events.
      </p>
      <div className="mt-4 flex justify-end">
        <Button
          onClick={save}
          disabled={saving || calendars.length === 0}
          className=""
          floatingInMobile={false}
          style={{}}>
          {saving ? "Saving…" : "Save"}
        </Button>
      </div>
    </Flyout>
  )
}

export default WriteCalendarFlyout
